/**
 * Le vrai solde : sur le compte d'un micro-entrepreneur, la part qui n'est
 * pas à lui.
 *
 * Le compte montre 8 400 € ; une partie partira à l'Urssaf pour ce qui est
 * encaissé et pas encore déclaré, une autre aux impôts l'an prochain, une
 * autre à la CFE en décembre. Personne ne fait ce calcul pour lui, et c'est
 * l'argent qu'on dépense sans le savoir.
 *
 * CE QUE LE CALCUL SAIT, ET CE QU'IL NE SAIT PAS.
 *   - L'Urssaf : le taux de l'activité (lib/tools/microEnterprise.js), la
 *     formation professionnelle, et le versement libératoire s'il est choisi,
 *     sur ce qui est encaissé depuis la dernière échéance de déclaration. Les
 *     mois viennent du dossier ; à défaut, du chiffre d'affaires annuel
 *     divisé par douze, et la ligne le dit (`estime`). L'ACRE, quand le
 *     dossier dit qu'on l'a : le taux réduit du simulateur (TAUX_ACRE) pour
 *     chaque mois qu'elle couvre, le taux plein ensuite.
 *   - L'impôt : au barème, l'année de création et la suivante jusqu'en
 *     août, tant qu'aucun acompte n'est ouvert. Ensuite, des acomptes sont
 *     prélevés chaque mois par les impôts, et le compte les a déjà payés.
 *   - La CFE : son montant dépend de la commune et ne se calcule pas. La
 *     ligne n'est comptée que si le lecteur a tapé celui de son avis.
 *
 * Micro-entreprise seulement : au réel ou en société, le compte n'obéit pas
 * aux mêmes règles, et la page le dit au lieu d'inventer.
 */

import { TAUX_COTISATIONS, tauxCfp, activiteFiscale } from "./microEnterprise.js";
import { ACTIVITES, TAUX_ACRE, regimeAcre } from "./incomeTax.js";
import { nettoyerDossier } from "./dossier.js";
import { situationDuDossier } from "./dossierSituation.js";

const p2 = (n) => String(n).padStart(2, "0");
const cleMois = (a, m) => `${a}-${p2(m)}`;
const finDuMois = (a, m) => `${a}-${p2(m)}-${p2(new Date(a, m, 0).getDate())}`;

/**
 * Le taux prélevé par l'Urssaf sur chaque euro encaissé. `avecAcre` : le
 * taux de cotisations réduit de l'ACRE à la place du taux plein, la
 * formation professionnelle et le versement libératoire restant pleins.
 */
export function tauxUrssaf(d, { avecAcre = false } = {}) {
    const activite = d.activite;
    if (!TAUX_COTISATIONS[activite]) return null;
    const vfl = d.optionFiscale === "vfl" ? ACTIVITES[activiteFiscale(activite)]?.vfl || 0 : 0;
    const cotisations =
        avecAcre && d.dateDebut && TAUX_ACRE[regimeAcre(new Date(`${d.dateDebut}T00:00:00Z`))]?.[activite] !== undefined
            ? TAUX_ACRE[regimeAcre(new Date(`${d.dateDebut}T00:00:00Z`))][activite]
            : TAUX_COTISATIONS[activite];
    return cotisations + tauxCfp(activite, Boolean(d.artisan)) + vfl;
}

/**
 * Les périodes dont la déclaration n'est pas encore échue, avec leurs mois
 * et leur date limite. Mensuelle : le mois dernier (déclaré avant la fin de
 * ce mois-ci) et le mois en cours. Trimestrielle : le trimestre dernier tant
 * que le premier mois du trimestre en cours n'est pas fini, et le trimestre
 * en cours.
 */
export function periodesOuvertes(aujourdhuiIso, periodicite = "mensuelle") {
    const [a, m] = aujourdhuiIso.split("-").map(Number);
    const periodes = [];
    if (periodicite === "trimestrielle") {
        const t = Math.floor((m - 1) / 3);
        const debut = t * 3 + 1;
        const courant = [debut, debut + 1, debut + 2].filter((x) => x <= m).map((x) => cleMois(a, x));
        const limiteCourant = debut + 3 > 12 ? finDuMois(a + 1, 1) : finDuMois(a, debut + 3);
        if (m === debut) {
            const precA = debut === 1 ? a - 1 : a;
            const precDebut = debut === 1 ? 10 : debut - 3;
            periodes.push({ mois: [0, 1, 2].map((i) => cleMois(precA, precDebut + i)), limite: finDuMois(a, m), passee: true });
        }
        periodes.push({ mois: courant, limite: limiteCourant, passee: false });
        return periodes;
    }
    const precA = m === 1 ? a - 1 : a;
    const precM = m === 1 ? 12 : m - 1;
    periodes.push({ mois: [cleMois(precA, precM)], limite: finDuMois(a, m), passee: true });
    periodes.push({ mois: [cleMois(a, m)], limite: m === 12 ? finDuMois(a + 1, 1) : finDuMois(a, m + 1), passee: false });
    return periodes;
}

/**
 * @param {object} brut le dossier
 * @param {object} o
 * @param {number} o.solde le solde du compte professionnel
 * @param {string} o.aujourdhui AAAA-MM-JJ
 * @param {"mensuelle"|"trimestrielle"} [o.periodicite]
 * @param {boolean} [o.dejaDeclare] la période passée est déjà déclarée et payée
 * @param {number|null} [o.cfe] le montant de l'avis de CFE, s'il est connu
 */
export function vraiSolde(brut, { solde, aujourdhui, periodicite = "mensuelle", dejaDeclare = false, cfe = null }) {
    const d = nettoyerDossier(brut);
    if (d.statut !== "micro") return { possible: false, raison: d.statut ? "statut" : "manque" };
    const taux = tauxUrssaf(d);
    if (taux === null || d.caCetteAnnee === null) return { possible: false, raison: "manque" };

    const r = situationDuDossier(d, aujourdhui);
    const [annee, moisCourant] = aujourdhui.split("-").map(Number);
    const debut = d.dateDebut;
    const actif = (mois) => !debut || mois >= debut.slice(0, 7);

    // L'URSSAF. Les mois encaissés si on les a, sinon une moyenne. Le mois
    // en cours, pas encore fini, ne compte que pour la part déjà écoulée
    // quand il vient de la moyenne.
    const jour = Number(aujourdhui.slice(8, 10));
    const joursDuMois = new Date(annee, moisCourant, 0).getDate();
    let estime = false;
    const montantDuMois = (mois) => {
        if (!actif(mois)) return 0;
        if (d.encaissements && d.encaissements[mois] !== undefined) return d.encaissements[mois];
        estime = true;
        const moyen = d.caCetteAnnee / 12;
        return mois === cleMois(annee, moisCourant) ? (moyen * jour) / joursDuMois : moyen;
    };
    // L'ACRE, mois par mois : un mois qui finit avant la fin de l'exonération
    // paie le taux réduit.
    const finAcreMois = d.acre === true && r.acre ? r.acre.fin.slice(0, 7) : null;
    const tauxAcre = finAcreMois ? tauxUrssaf(d, { avecAcre: true }) : null;
    const tauxDuMois = (mois) => (finAcreMois && mois <= finAcreMois ? tauxAcre : taux);
    const periodes = periodesOuvertes(aujourdhui, periodicite).filter((p) => !(p.passee && dejaDeclare));
    const encaisse = periodes.reduce((s, p) => s + p.mois.reduce((x, m) => x + montantDuMois(m), 0), 0);
    const du = periodes.reduce((s, p) => s + p.mois.reduce((x, m) => x + montantDuMois(m) * tauxDuMois(m), 0), 0);
    const lignes = [];
    lignes.push({
        cle: "urssaf",
        montant: Math.round(du),
        base: Math.round(encaisse),
        // Le taux effectif : celui de l'ACRE, le plein, ou leur mélange.
        taux: encaisse > 0 ? du / encaisse : taux,
        acre: Boolean(finAcreMois && periodes.some((p) => p.mois.some((m) => m <= finAcreMois))),
        echeance: periodes.length ? periodes[0].limite : null,
        estime,
        compte: true,
    });

    // L'IMPÔT, au barème : rien n'est prélevé tant que les impôts ne
    // connaissent pas l'activité. L'année de création, et la suivante
    // jusqu'en août, car les acomptes ne démarrent qu'en septembre, après la
    // première déclaration. La part de l'impôt déjà gagnée est à garder de
    // côté, sauf si le lecteur a dit avoir ouvert un acompte (impot-acomptes).
    const anneeDebut = debut ? Number(debut.slice(0, 4)) : null;
    const creation = anneeDebut === annee;
    const deuxieme = anneeDebut === annee - 1 && moisCourant <= 8;
    const acompteOuvert = Object.keys(d.faits || {}).some((k) => k.startsWith("impot-acomptes@"));
    if (d.optionFiscale !== "vfl" && r.revenu && !acompteOuvert && (creation || deuxieme)) {
        const moisDebut = creation ? Number(debut.slice(5, 7)) : 1;
        const moisActifs = moisCourant - moisDebut + 1;
        const moisAnnee = 12 - moisDebut + 1;
        lignes.push({
            cle: "impot",
            montant: Math.round((r.revenu.impot * moisActifs) / moisAnnee),
            echeance: `${annee + 1}-09-15`,
            deuxieme,
            compte: true,
        });
    } else if (d.optionFiscale !== "vfl") {
        lignes.push({ cle: "impotAcomptes", montant: 0, compte: false });
    }

    // LA CFE : le premier paiement, s'il tombe dans les quatre mois. La
    // déclaration initiale (cfe-declaration) ne coûte rien.
    const prochaineCfe = r.echeances.find((e) => e.cle === "cfe-premier-solde" && e.joursRestants >= 0 && e.joursRestants <= 120);
    if (prochaineCfe) {
        const connu = Number.isFinite(cfe) && cfe > 0;
        lignes.push({ cle: "cfe", montant: connu ? Math.round(cfe) : 0, echeance: prochaineCfe.date, compte: connu });
    }

    // LA TVA déjà due : elle n'est pas à vous, mais son montant dépend de ce
    // qui a été facturé depuis. La ligne le rappelle sans chiffre.
    if (r.tva && r.tva.passe) lignes.push({ cle: "tva", montant: 0, echeance: r.tva.date, compte: false });

    const total = lignes.filter((l) => l.compte).reduce((s, l) => s + l.montant, 0);
    const aVous = Math.round((Number(solde) || 0) - total);
    return {
        possible: true,
        lignes,
        total,
        aVous,
        part: solde > 0 ? Math.max(0, aVous) / solde : null,
        // Ce que le dossier dit de l'ACRE : obtenue (et jusqu'à quand), ou non su.
        acre: d.acre === true && r.acre ? { fin: r.acre.fin } : d.acre === false ? false : null,
    };
}
